/**
 * @fileoverview Página responsável por listar, buscar e manter contatos cadastrados.
 */

import React, { useState, useEffect, useReducer, useContext, useMemo, useCallback, useRef } from "react";
import { FixedSizeList } from "react-window";
import AutoSizer from "react-virtualized-auto-sizer";

import { toast } from "react-toastify";
import { useHistory } from "react-router-dom";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";
import Typography from "@material-ui/core/Typography";
import Paper from "@material-ui/core/Paper";
import Avatar from "@material-ui/core/Avatar";
import Skeleton from "@material-ui/lab/Skeleton";
import TextField from "@material-ui/core/TextField";
import InputAdornment from "@material-ui/core/InputAdornment";
import Menu from "@material-ui/core/Menu";
import MenuItem from "@material-ui/core/MenuItem";
import ListItemIcon from "@material-ui/core/ListItemIcon";
import ListItemText from "@material-ui/core/ListItemText";
import Popover from "@material-ui/core/Popover";
import Grow from "@material-ui/core/Grow";
import {
  LayoutList,
  MessageCirclePlus,
  Pencil,
  Trash2 as Trash,
  Wallet,
  Upload,
  UserPlus,
  MoreVertical,
  Search,
} from "lucide-react";
import api from "../../services/api";
import ContactModal from "../../components/ContactModal";
import ConfirmationModal from "../../components/ConfirmationModal/";

import { i18n } from "../../translate/i18n";
import MainContainer from "../../components/MainContainer";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Can } from "../../components/Can";
import NewTicketModal from "../../components/NewTicketModal";
import { SocketContext } from "../../context/Socket/SocketContext";

import ImportContactsModal from "../../components/ImportContactsModal";
import WalletModal from "../../components/WalletModal";

const reducer = (state, action) => {
  if (action.type === "LOAD_CONTACTS") {
    const contacts = action.payload;
    let updatedState = [...state];

    contacts.forEach((contact) => {
      const contactIndex = updatedState.findIndex((c) => c.id === contact.id);
      if (contactIndex !== -1) {
        updatedState = [
          ...updatedState.slice(0, contactIndex),
          contact,
          ...updatedState.slice(contactIndex + 1)
        ];
      } else {
        updatedState = [...updatedState, contact];
      }
    });

    return updatedState;
  }

  if (action.type === "UPDATE_CONTACTS") {
    const contact = action.payload;
    const contactIndex = state.findIndex((c) => c.id === contact.id);

    if (contactIndex !== -1) {
      return [
        ...state.slice(0, contactIndex),
        contact,
        ...state.slice(contactIndex + 1)
      ];
    } else {
      return [contact, ...state];
    }
  }

  if (action.type === "DELETE_CONTACT") {
    const contactId = action.payload;
    return state.filter((c) => c.id !== contactId);
  }

  if (action.type === "RESET") {
    return [];
  }

  return state;
};

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1.5),
    },
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(3),
    flexWrap: 'wrap',
    gap: theme.spacing(2),
    [theme.breakpoints.down("sm")]: {
      flexDirection: "column",
      alignItems: "stretch",
      marginBottom: theme.spacing(1.5),
      gap: theme.spacing(1.25),
    },
  },
  headerTitleRow: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    width: "100%",
  },
  mobileMenuButton: {
    width: 40,
    height: 40,
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: 10,
    border: theme.palette.type === "dark"
      ? "1px solid rgba(255, 255, 255, 0.08)"
      : "1px solid rgba(15, 23, 42, 0.08)",
    background: theme.palette.type === "dark"
      ? "rgba(255, 255, 255, 0.03)"
      : "rgba(15, 23, 42, 0.02)",
    color: theme.palette.type === "dark" ? "rgba(248, 250, 252, 0.85)" : "#475569",
    cursor: "pointer",
    transition: "all 180ms cubic-bezier(0.4, 0, 0.2, 1)",
    padding: 0,
    "&:hover": {
      background: theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.06)"
        : "rgba(15, 23, 42, 0.04)",
      borderColor: theme.palette.type === "dark"
        ? "rgba(91, 124, 255, 0.35)"
        : "rgba(59, 130, 246, 0.3)",
    },
  },
  title: {
    fontSize: "1.75rem",
    fontWeight: 700,
    color: theme.palette.type === 'dark' ? '#FFFFFF' : '#1e293b',
    [theme.breakpoints.down("sm")]: {
      fontSize: "1.35rem",
    },
  },
  buttonsWrapper: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexWrap: 'wrap',
    [theme.breakpoints.down("sm")]: {
      width: "100%",
      gap: theme.spacing(0.75),
      flexWrap: "nowrap",
    },
  },
  mainPaper: {
    flex: 1,
    padding: theme.spacing(2),
    paddingTop: theme.spacing(2),
    overflowY: "hidden",
    backgroundColor: "transparent",
    boxShadow: "none",
    minHeight: 0,
    [theme.breakpoints.down("sm")]: {
      padding: theme.spacing(1),
      paddingTop: theme.spacing(1),
    },
  },
  searchField: {
    minWidth: 260,
    [theme.breakpoints.down("sm")]: {
      width: "auto",
      minWidth: 0,
      flex: 1,
    },
    '& .MuiOutlinedInput-root': {
      borderRadius: '14px',
      background: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
      border: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.15)' : '1px solid rgba(0, 0, 0, 0.08)',
      transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
      '&:hover': {
        background: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
        border: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.3)' : '1px solid rgba(0, 0, 0, 0.12)',
      },
      '&.Mui-focused': {
        background: theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.02)',
        border: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.35)' : '1px solid rgba(15, 23, 42, 0.18)',
        boxShadow: 'none',
      },
      '& fieldset': {
        border: 'none',
      },
    },
  },
  tableContainer: {
    background: theme.palette.type === 'dark'
      ? 'linear-gradient(135deg, rgba(18, 18, 19, 0.6) 0%, rgba(22, 22, 24, 0.6) 100%)'
      : '#FFFFFF',
    border: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.08)',
    borderRadius: "20px",
    overflow: 'hidden',
    boxShadow: theme.palette.type === 'dark'
      ? '0 8px 32px rgba(0, 0, 0, 0.4)'
      : '0 4px 20px rgba(0, 0, 0, 0.08)',
    [theme.breakpoints.down("sm")]: {
      borderRadius: "14px",
    },
    '& .MuiTableHead-root': {
      '& .MuiTableCell-head': {
        backgroundColor: theme.palette.type === 'dark' ? 'rgba(91, 124, 255, 0.1)' : '#f5f5f5',
        color: theme.palette.type === 'dark' ? '#eee' : '#000',
        fontWeight: 600,
        borderBottom: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.2)' : '1px solid rgba(0, 0, 0, 0.12)',
      },
    },
    '& .MuiTableBody-root': {
      '& .MuiTableRow-root': {
        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
        '&:hover': {
          backgroundColor: theme.palette.type === 'dark' ? 'rgba(91, 124, 255, 0.08)' : 'rgba(61, 92, 253, 0.04)',
        },
      },
      '& .MuiTableCell-body': {
        borderBottom: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.06)',
        color: theme.palette.type === 'dark' ? '#eee' : '#000',
      },
    },
  },
  actionIcon: {
    color: theme.palette.type === 'dark' ? '#93c5fd' : '#3b82f6',
    padding: theme.spacing(1),
    borderRadius: '8px',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      backgroundColor: theme.palette.type === 'dark'
        ? 'rgba(59, 130, 246, 0.15)'
        : 'rgba(59, 130, 246, 0.08)',
      transform: 'scale(1.1)',
    },
  },
  deleteIcon: {
    color: theme.palette.type === 'dark' ? '#fca5a5' : '#ef4444',
    '&:hover': {
      backgroundColor: theme.palette.type === 'dark'
        ? 'rgba(239, 68, 68, 0.15)'
        : 'rgba(239, 68, 68, 0.08)',
    },
  },
  virtualListContainer: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
  },
  tableHeader: {
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px',
    backgroundColor: theme.palette.type === 'dark' ? 'rgba(91, 124, 255, 0.1)' : '#f5f5f5',
    fontWeight: 600,
    borderBottom: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.2)' : '1px solid rgba(0, 0, 0, 0.12)',
    [theme.breakpoints.down("sm")]: {
      padding: '10px 8px',
    },
  },
  headerCell: {
    color: theme.palette.type === 'dark' ? '#eee' : '#000',
    padding: '0 8px',
    fontSize: '0.875rem',
    [theme.breakpoints.down("sm")]: {
      padding: '0 4px',
      fontSize: '0.75rem',
    },
  },
  contactRow: {
    display: 'flex',
    alignItems: 'center',
    padding: '8px 16px',
    borderBottom: theme.palette.type === 'dark' ? '1px solid rgba(91, 124, 255, 0.1)' : '1px solid rgba(0, 0, 0, 0.06)',
    transition: 'background-color 0.15s ease',
    cursor: 'default',
    fontFamily: '"Montserrat", sans-serif',
    '&:hover': {
      backgroundColor: theme.palette.type === 'dark' ? 'rgba(91, 124, 255, 0.08)' : 'rgba(61, 92, 253, 0.04)',
    },
    [theme.breakpoints.down("sm")]: {
      padding: '6px 8px',
    },
  },
  contactCell: {
    padding: '0 8px',
    color: theme.palette.type === 'dark' ? '#eee' : '#000',
    fontSize: '0.875rem',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    fontFamily: '"Montserrat", sans-serif',
    [theme.breakpoints.down("sm")]: {
      padding: '0 4px',
      fontSize: '0.8rem',
    },
  },
  listWrapper: {
    flex: 1,
    minHeight: 0,
  },
  loadingRow: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '16px',
    color: theme.palette.type === 'dark' ? '#aaa' : '#666',
  },
  skeletonRow: {
    display: 'flex',
    alignItems: 'center',
    boxSizing: 'border-box',
    paddingLeft: 8,
    paddingRight: 8,
    borderBottom: theme.palette.type === 'dark'
      ? '1px solid rgba(255, 255, 255, 0.04)'
      : '1px solid rgba(0, 0, 0, 0.04)',
  },
  skeletonPulse: {
    background: theme.palette.type === 'dark'
      ? 'rgba(255, 255, 255, 0.08)'
      : 'rgba(0, 0, 0, 0.06)',
    borderRadius: 6,
  },
  skeletonFade: {
    position: 'absolute',
    left: 0,
    right: 0,
    bottom: 0,
    height: '45%',
    pointerEvents: 'none',
    background: theme.palette.type === 'dark'
      ? 'linear-gradient(180deg, rgba(18,18,19,0) 0%, rgba(18,18,19,0.55) 55%, rgba(18,18,19,0.95) 100%)'
      : 'linear-gradient(180deg, rgba(244,246,250,0) 0%, rgba(244,246,250,0.6) 55%, rgba(244,246,250,0.98) 100%)',
  },
  rowActions: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 4,
  },
  rowActionButton: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    padding: 0,
    border: 'none',
    borderRadius: 8,
    background: 'transparent',
    color: theme.palette.type === 'dark' ? '#93c5fd' : '#3b82f6',
    cursor: 'pointer',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      backgroundColor: theme.palette.type === 'dark'
        ? 'rgba(59, 130, 246, 0.15)'
        : 'rgba(59, 130, 246, 0.08)',
      transform: 'scale(1.05)',
    },
  },
  rowActionButtonDanger: {
    color: theme.palette.type === 'dark' ? '#fca5a5' : '#ef4444',
    '&:hover': {
      backgroundColor: theme.palette.type === 'dark'
        ? 'rgba(239, 68, 68, 0.15)'
        : 'rgba(239, 68, 68, 0.08)',
    },
  },
  menuActionButton: {
    color: theme.palette.type === 'dark' ? '#93c5fd' : '#3b82f6',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    padding: 0,
    border: 'none',
    borderRadius: 8,
    background: 'transparent',
    cursor: 'pointer',
    transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      backgroundColor: theme.palette.type === 'dark'
        ? 'rgba(59, 130, 246, 0.15)'
        : 'rgba(59, 130, 246, 0.08)',
    },
  },
  menuItemIcon: {
    minWidth: 34,
    color: theme.palette.type === 'dark' ? '#eee' : '#334155',
  },
  actionBtnBase: {
    whiteSpace: "nowrap",
    flexShrink: 0,
    height: 36,
    minWidth: 0,
    padding: "0 14px",
    borderRadius: 10,
    fontSize: "0.875rem",
    fontWeight: 500,
    textTransform: "none",
    display: "inline-flex",
    alignItems: "center",
    gap: 8,
    cursor: "pointer",
    border: "none",
    fontFamily: "inherit",
    transition: "all 180ms cubic-bezier(0.4, 0, 0.2, 1)",
    "& svg": { flexShrink: 0 },
  },
  actionBtnPrimary: {
    background: "linear-gradient(135deg, #4f7cff 0%, #3b82f6 100%)",
    color: "#ffffff",
    "&:hover:not(:disabled)": {
      background: "linear-gradient(135deg, #3f6cf0 0%, #2563eb 100%)",
    },
  },
  actionBtnSecondary: {
    background: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.04)" : "rgba(15, 23, 42, 0.03)",
    color: theme.palette.type === "dark" ? "#f8fafc" : "#111827",
    border: theme.palette.type === "dark" ? "1px solid rgba(255, 255, 255, 0.12)" : "1px solid rgba(15, 23, 42, 0.12)",
    "&:hover:not(:disabled)": {
      background: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.08)" : "rgba(15, 23, 42, 0.06)",
      borderColor: theme.palette.type === "dark" ? "rgba(91, 124, 255, 0.4)" : "rgba(59, 130, 246, 0.35)",
      color: theme.palette.type === "dark" ? "#ffffff" : "#0f172a",
    },
  },
  actionBtnTertiary: {
    background: "transparent",
    color: theme.palette.type === "dark" ? "rgba(248, 250, 252, 0.7)" : "#64748b",
    border: theme.palette.type === "dark" ? "1px solid rgba(255, 255, 255, 0.06)" : "1px solid rgba(15, 23, 42, 0.08)",
    "&:hover:not(:disabled)": {
      background: theme.palette.type === "dark" ? "rgba(255, 255, 255, 0.04)" : "rgba(15, 23, 42, 0.03)",
      color: theme.palette.type === "dark" ? "#f8fafc" : "#111827",
    },
  },
  cmdMenuPaper: {
    borderRadius: 14,
    overflow: "visible",
    background: theme.palette.type === "dark" ? "#1c1f22" : "#ffffff",
    border: theme.palette.type === "dark"
      ? "1px solid rgba(255, 255, 255, 0.06)"
      : "1px solid rgba(15, 23, 42, 0.06)",
    boxShadow: theme.palette.type === "dark"
      ? "0 20px 40px -12px rgba(0, 0, 0, 0.5), 0 6px 14px -4px rgba(0, 0, 0, 0.3)"
      : "0 20px 40px -12px rgba(15, 23, 42, 0.22), 0 6px 14px -4px rgba(15, 23, 42, 0.10)",
  },
  cmdMenu: {
    display: "flex",
    flexDirection: "column",
    padding: "6px 4px",
    minWidth: 232,
    fontFamily: "'Montserrat', sans-serif",
  },
  cmdMenuSection: {
    display: "flex",
    flexDirection: "column",
  },
  cmdMenuEyebrow: {
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.14em",
    textTransform: "uppercase",
    padding: "8px 12px 4px",
    color: theme.palette.type === "dark" ? "#6b7380" : "#94A3B8",
  },
  cmdMenuItem: {
    display: "flex",
    alignItems: "center",
    gap: 12,
    padding: "8px 10px",
    borderRadius: 8,
    border: "none",
    background: "transparent",
    width: "100%",
    cursor: "pointer",
    textAlign: "left",
    fontFamily: "inherit",
    fontSize: 13,
    fontWeight: 500,
    color: theme.palette.type === "dark" ? "#e6e8eb" : "#1a1a1a",
    transition: "background-color 140ms ease",
    animation: "$cmdMenuItemIn 200ms cubic-bezier(0.16, 1, 0.3, 1) both",
    "&:hover": {
      backgroundColor: theme.palette.type === "dark"
        ? "rgba(255, 255, 255, 0.05)"
        : "rgba(15, 23, 42, 0.04)",
    },
    "&:focus-visible": {
      outline: `2px solid ${theme.palette.type === "dark" ? "#5B7CFF" : "#3D5CFD"}`,
      outlineOffset: 1,
    },
    "&:nth-child(1)": { animationDelay: "0ms" },
    "&:nth-child(2)": { animationDelay: "35ms" },
    "&:nth-child(3)": { animationDelay: "70ms" },
    "&:nth-child(4)": { animationDelay: "105ms" },
    "&:nth-child(5)": { animationDelay: "140ms" },
  },
  cmdMenuItemDanger: {
    color: theme.palette.type === "dark" ? "#fca5a5" : "#dc2626",
    "&:hover": {
      backgroundColor: theme.palette.type === "dark"
        ? "rgba(248, 113, 113, 0.08)"
        : "rgba(220, 38, 38, 0.05)",
    },
  },
  cmdMenuIconBox: {
    width: 28,
    height: 28,
    flex: "0 0 28px",
    borderRadius: 8,
    display: "grid",
    placeItems: "center",
  },
  cmdMenuIconBoxCobalt: {
    background: theme.palette.type === "dark" ? "rgba(91, 124, 255, 0.16)" : "#EEF2FF",
    color: theme.palette.type === "dark" ? "#93a6ff" : "#3D5CFD",
  },
  cmdMenuIconBoxAmber: {
    background: theme.palette.type === "dark" ? "rgba(252, 211, 77, 0.14)" : "#FEF3C7",
    color: theme.palette.type === "dark" ? "#fbbf24" : "#B45309",
  },
  cmdMenuIconBoxSlate: {
    background: theme.palette.type === "dark" ? "rgba(148, 163, 184, 0.16)" : "#F1F5F9",
    color: theme.palette.type === "dark" ? "#cbd5e1" : "#475569",
  },
  cmdMenuIconBoxGreen: {
    background: theme.palette.type === "dark" ? "rgba(34, 197, 94, 0.14)" : "#DCFCE7",
    color: theme.palette.type === "dark" ? "#86efac" : "#16a34a",
  },
  cmdMenuIconBoxRed: {
    background: theme.palette.type === "dark" ? "rgba(248, 113, 113, 0.14)" : "#FEE2E2",
    color: theme.palette.type === "dark" ? "#fca5a5" : "#dc2626",
  },
  cmdMenuLabel: {
    flex: 1,
  },
  cmdMenuDivider: {
    height: 1,
    margin: "6px 8px",
    background: theme.palette.type === "dark"
      ? "linear-gradient(90deg, transparent, rgba(255,255,255,0.08), transparent)"
      : "linear-gradient(90deg, transparent, #E2E8F0, transparent)",
  },
  "@keyframes cmdMenuItemIn": {
    "0%": { opacity: 0, transform: "translateY(4px)" },
    "100%": { opacity: 1, transform: "translateY(0)" },
  },
}));

const areSameRowStyle = (prevStyle, nextStyle) => {
  if (prevStyle === nextStyle) return true;
  if (!prevStyle || !nextStyle) return false;

  return (
    prevStyle.top === nextStyle.top &&
    prevStyle.left === nextStyle.left &&
    prevStyle.width === nextStyle.width &&
    prevStyle.height === nextStyle.height
  );
};

const ContactListRow = React.memo(({
  classes,
  contact,
  style,
  isMobile,
  userProfile,
  onStartConversation,
  onQuickCreateTicket,
  onEditContact,
  onOpenMobileActions,
  onOpenDeleteConfirm,
}) => {
  return (
    <div style={style} className={classes.contactRow}>
      <div style={{ width: isMobile ? 40 : 56, display: 'flex', justifyContent: 'center' }}>
        <Avatar src={contact.profilePicUrl} style={{ width: isMobile ? 32 : 40, height: isMobile ? 32 : 40 }} />
      </div>
      <div className={classes.contactCell} style={{ flex: 1, minWidth: 0 }}>
        {contact.name}
      </div>
      <div
        className={classes.contactCell}
        style={
          isMobile
            ? { flex: 1, minWidth: 0, textAlign: "left", fontSize: "0.85rem", fontWeight: 500 }
            : { width: 280, textAlign: 'center', paddingRight: 80, fontSize: '0.95rem', fontWeight: 500 }
        }
      >
        {contact.number}
      </div>
      <div style={{ width: isMobile ? 48 : 184, textAlign: 'center', display: 'flex', justifyContent: 'center' }}>
        {isMobile ? (
          <button
            type="button"
            className={classes.menuActionButton}
            title={i18n.t("contacts.table.actions")}
            aria-label={i18n.t("contacts.table.actions")}
            onClick={(event) => onOpenMobileActions(event, contact)}
          >
            <MoreVertical size={18} strokeWidth={1.75} />
          </button>
        ) : (
          <div className={classes.rowActions}>
            <button
              type="button"
              className={classes.rowActionButton}
              title="Ver tickets"
              aria-label="Ver tickets"
              onClick={() => onStartConversation(contact)}
            >
              <LayoutList size={18} strokeWidth={1.5} color="#487ad7" />
            </button>
            <button
              type="button"
              className={classes.rowActionButton}
              title="Iniciar conversa"
              aria-label="Iniciar conversa"
              onClick={() => onQuickCreateTicket(contact)}
            >
              <MessageCirclePlus size={18} strokeWidth={1.75} color="#25d366" />
            </button>
            <button
              type="button"
              className={classes.rowActionButton}
              title={i18n.t("contacts.buttons.edit")}
              aria-label={i18n.t("contacts.buttons.edit")}
              onClick={() => onEditContact(contact.id)}
            >
              <Pencil size={18} strokeWidth={1.5} color="#5490ff" />
            </button>
            <Can
              role={userProfile}
              perform="contacts-page:deleteContact"
              yes={() => (
                <button
                  type="button"
                  className={`${classes.rowActionButton} ${classes.rowActionButtonDanger}`}
                  title={i18n.t("contacts.buttons.deleteSingle")}
                  aria-label={i18n.t("contacts.buttons.deleteSingle")}
                  onClick={() => onOpenDeleteConfirm(contact)}
                >
                  <Trash size={18} strokeWidth={1.5} color="#c72b40" />
                </button>
              )}
            />
          </div>
        )}
      </div>
    </div>
  );
}, (prevProps, nextProps) => {
  return (
    prevProps.classes === nextProps.classes &&
    prevProps.isMobile === nextProps.isMobile &&
    prevProps.userProfile === nextProps.userProfile &&
    areSameRowStyle(prevProps.style, nextProps.style) &&
    prevProps.contact?.id === nextProps.contact?.id &&
    prevProps.contact?.name === nextProps.contact?.name &&
    prevProps.contact?.number === nextProps.contact?.number &&
    prevProps.contact?.profilePicUrl === nextProps.contact?.profilePicUrl
  );
});

const ContactSkeletonRow = React.memo(({ classes, isMobile, style }) => {
  const avatarSize = isMobile ? 32 : 40;
  return (
    <div style={style} className={classes.skeletonRow}>
      <div style={{ width: isMobile ? 40 : 56, display: 'flex', justifyContent: 'center' }}>
        <Skeleton
          variant="circle"
          width={avatarSize}
          height={avatarSize}
          className={classes.skeletonPulse}
        />
      </div>
      <div style={{ flex: 1, minWidth: 0, paddingRight: 12 }}>
        <Skeleton variant="text" width="55%" height={18} className={classes.skeletonPulse} />
      </div>
      <div
        style={
          isMobile
            ? { flex: 1, minWidth: 0, textAlign: "left" }
            : { width: 280, textAlign: 'center', paddingRight: 80 }
        }
      >
        <Skeleton
          variant="text"
          width={isMobile ? "70%" : 140}
          height={16}
          className={classes.skeletonPulse}
          style={isMobile ? undefined : { margin: '0 auto' }}
        />
      </div>
      <div
        style={{
          width: isMobile ? 48 : 184,
          display: 'flex',
          justifyContent: 'center',
          gap: 4,
        }}
      >
        {isMobile ? (
          <Skeleton variant="circle" width={28} height={28} className={classes.skeletonPulse} />
        ) : (
          <>
            <Skeleton variant="circle" width={28} height={28} className={classes.skeletonPulse} />
            <Skeleton variant="circle" width={28} height={28} className={classes.skeletonPulse} />
            <Skeleton variant="circle" width={28} height={28} className={classes.skeletonPulse} />
            <Skeleton variant="circle" width={28} height={28} className={classes.skeletonPulse} />
          </>
        )}
      </div>
    </div>
  );
}, (prev, next) => (
  prev.isMobile === next.isMobile &&
  areSameRowStyle(prev.style, next.style)
));

const ContactVirtualRow = React.memo(({ index, style, data }) => {
  if (index >= data.contacts.length) {
    return (
      <ContactSkeletonRow
        classes={data.classes}
        isMobile={data.isMobile}
        style={style}
      />
    );
  }

  return (
    <ContactListRow
      classes={data.classes}
      contact={data.contacts[index]}
      style={style}
      isMobile={data.isMobile}
      userProfile={data.userProfile}
      onStartConversation={data.onStartConversation}
      onQuickCreateTicket={data.onQuickCreateTicket}
      onEditContact={data.onEditContact}
      onOpenMobileActions={data.onOpenMobileActions}
      onOpenDeleteConfirm={data.onOpenDeleteConfirm}
    />
  );
}, (prevProps, nextProps) => {
  if (prevProps.index !== nextProps.index) {
    return false;
  }

  if (!areSameRowStyle(prevProps.style, nextProps.style)) {
    return false;
  }

  const prevContact = prevProps.data.contacts[prevProps.index];
  const nextContact = nextProps.data.contacts[nextProps.index];

  if (!prevContact || !nextContact) {
    return (
      prevContact === nextContact &&
      prevProps.data.loading === nextProps.data.loading
    );
  }

  return (
    prevProps.data.isMobile === nextProps.data.isMobile &&
    prevProps.data.userProfile === nextProps.data.userProfile &&
    prevContact.id === nextContact.id &&
    prevContact.name === nextContact.name &&
    prevContact.number === nextContact.number &&
    prevContact.profilePicUrl === nextContact.profilePicUrl
  );
});

const Contacts = () => {
  const classes = useStyles();
  const history = useHistory();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { user } = useContext(AuthContext);

  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(1);
  const [searchParam, setSearchParam] = useState("");
  const [contacts, dispatch] = useReducer(reducer, []);
  const [selectedContactId, setSelectedContactId] = useState(null);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [newTicketModalInitialView, setNewTicketModalInitialView] = useState("picker");
  const [contactTicket, setContactTicket] = useState({});
  const [deletingContact, setDeletingContact] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [openModalImport, setOpenModalImport] = useState(false);
  const [openWalletModal, setOpenWalletModal] = useState(false);
  const [mobileActionsAnchorPosition, setMobileActionsAnchorPosition] = useState(null);
  const [mobileActionsContact, setMobileActionsContact] = useState(null);
  const [mobileHeaderMenuAnchor, setMobileHeaderMenuAnchor] = useState(null);

  const socketManager = useContext(SocketContext);

  useEffect(() => {
    dispatch({ type: "RESET" });
    setPageNumber(1);
  }, [searchParam]);

  useEffect(() => {
    let isMounted = true;
    setLoading(true);

    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get("/contacts/", {
            params: { searchParam, pageNumber },
          });

          if (isMounted) {
            dispatch({ type: "LOAD_CONTACTS", payload: data.contacts });
            setHasMore(data.hasMore);
          }
        } catch (err) {
          if (isMounted) {
            toastError(err);
          }
        } finally {
          if (isMounted) {
            setLoading(false);
          }
        }
      };
      fetchContacts();
    }, 500);

    return () => {
      isMounted = false;
      clearTimeout(delayDebounceFn);
    };
  }, [searchParam, pageNumber]);

  const searchParamRef = useRef(searchParam);
  useEffect(() => { searchParamRef.current = searchParam; }, [searchParam]);

  useEffect(() => {
    const companyId = localStorage.getItem("companyId");
    const socket = socketManager.getSocket(companyId);

    socket.on(`company-${companyId}-contact`, (data) => {
      if (data.action === "update" || data.action === "create") {
        if (searchParamRef.current) return;
        dispatch({ type: "UPDATE_CONTACTS", payload: data.contact });
      }

      if (data.action === "delete") {
        dispatch({ type: "DELETE_CONTACT", payload: +data.contactId });
      }
    });

    const companyIdForCleanup = companyId;
    return () => {
      socket.off(`company-${companyIdForCleanup}-contact`);
    };
  }, [socketManager]);

  const handleSearch = (event) => {
    setSearchParam(event.target.value.toLowerCase());
  };

  const handleOpenContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(true);
  };

  const handleCloseContactModal = () => {
    setSelectedContactId(null);
    setContactModalOpen(false);
  };

  // const handleSaveTicket = async contactId => {
  // 	if (!contactId) return;
  // 	setLoading(true);
  // 	try {
  // 		const { data: ticket } = await api.post("/tickets", {
  // 			contactId: contactId,
  // 			userId: user?.id,
  // 			status: "open",
  // 		});
  // 		history.push(`/tickets/${ticket.id}`);
  // 	} catch (err) {
  // 		toastError(err);
  // 	}
  // 	setLoading(false);
  // };

  const handleCloseOrOpenTicket = (ticket) => {
    setNewTicketModalOpen(false);
    if (ticket !== undefined && ticket.uuid !== undefined) {
      history.push(`/tickets/${ticket.uuid}`);
    }
  };

  const handleStartConversation = useCallback((contact) => {
    if (!contact?.id) {
      return;
    }

    setContactTicket(contact);
    setNewTicketModalInitialView("contactTickets");
    setNewTicketModalOpen(true);
  }, []);

  const handleQuickCreateTicket = useCallback((contact) => {
    if (!contact?.id) {
      return;
    }

    setContactTicket(contact);
    setNewTicketModalInitialView("quickCreateTicket");
    setNewTicketModalOpen(true);
  }, []);

  const hadleEditContact = useCallback((contactId) => {
    setSelectedContactId(contactId);
    setContactModalOpen(true);
  }, []);

  const handleDeleteContact = async (contactId) => {
    try {
      await api.delete(`/contacts/${contactId}`);
      toast.success(i18n.t("contacts.toasts.deleted"));
    } catch (err) {
      toastError(err);
    }
    setDeletingContact(null);
    setSearchParam("");
    setPageNumber(1);
  };

  const handleOpenImportModal = (  ) => {
    setOpenModalImport(true);
  }

  const handleOpenMobileActions = useCallback((event, contact) => {
    const rect = event.currentTarget.getBoundingClientRect();
    setMobileActionsAnchorPosition({
      top: Math.round(rect.bottom),
      left: Math.round(rect.right),
    });
    setMobileActionsContact(contact);
  }, []);

  const handleCloseMobileActions = useCallback(() => {
    setMobileActionsAnchorPosition(null);
    setMobileActionsContact(null);
  }, []);

  const handleOpenDeleteConfirm = useCallback((contact) => {
    setConfirmOpen(true);
    setDeletingContact(contact);
  }, []);

  const loadMore = useCallback(() => {
    setPageNumber((prevState) => prevState + 1);
  }, []);

  const handleCloseModalImport = (  ) => {
    setOpenModalImport(false);
  }

  const isMobileActionsOpen = Boolean(mobileActionsAnchorPosition);

  const rowData = useMemo(() => ({
    classes,
    contacts,
    isMobile,
    loading,
    userProfile: user.profile,
    onStartConversation: handleStartConversation,
    onQuickCreateTicket: handleQuickCreateTicket,
    onEditContact: hadleEditContact,
    onOpenMobileActions: handleOpenMobileActions,
    onOpenDeleteConfirm: handleOpenDeleteConfirm,
  }), [
    classes,
    contacts,
    isMobile,
    loading,
    user.profile,
    handleStartConversation,
    handleQuickCreateTicket,
    hadleEditContact,
    handleOpenMobileActions,
    handleOpenDeleteConfirm,
  ]);

  return (
    <MainContainer className={classes.root}>
      <ImportContactsModal
        open={openModalImport}
        onClose={handleCloseModalImport}
      />
      <WalletModal
        open={openWalletModal}
        onClose={() => setOpenWalletModal(false)}
      />
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        initialContact={contactTicket}
        initialView={newTicketModalInitialView}
        onClose={(ticket) => {
          handleCloseOrOpenTicket(ticket);
        }}
      />
      <ContactModal
        open={contactModalOpen}
        onClose={handleCloseContactModal}
        aria-labelledby="form-dialog-title"
        contactId={selectedContactId}
      ></ContactModal>
      <ConfirmationModal
        title={
          deletingContact
            ? `${i18n.t("contacts.confirmationModal.deleteTitle")} ${deletingContact.name}?`
            : ""
        }
        open={confirmOpen}
        onClose={setConfirmOpen}
        onConfirm={() =>
          deletingContact && handleDeleteContact(deletingContact.id)
        }
      >
        {deletingContact
          ? `${i18n.t("contacts.confirmationModal.deleteMessage")}`
          : ""}
      </ConfirmationModal>
      <div className={classes.header}>
        {isMobile ? (
          <>
            <div className={classes.headerTitleRow}>
              <Typography className={classes.title}>
                {i18n.t("contacts.title")}
              </Typography>
              <button
                type="button"
                className={classes.mobileMenuButton}
                onClick={(e) => setMobileHeaderMenuAnchor(e.currentTarget)}
                aria-label="Mais opções"
                aria-haspopup="menu"
                aria-expanded={Boolean(mobileHeaderMenuAnchor)}
              >
                <MoreVertical size={20} strokeWidth={1.75} />
              </button>
            </div>
            <TextField
              placeholder={i18n.t("contacts.searchPlaceholder")}
              type="search"
              value={searchParam}
              onChange={handleSearch}
              className={classes.searchField}
              variant="outlined"
              size="small"
              fullWidth
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search size={18} strokeWidth={1.75} color={theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.5)' : '#64748b'} />
                  </InputAdornment>
                ),
              }}
            />
            <Popover
              open={Boolean(mobileHeaderMenuAnchor)}
              anchorEl={mobileHeaderMenuAnchor}
              onClose={() => setMobileHeaderMenuAnchor(null)}
              anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
              transformOrigin={{ vertical: "top", horizontal: "right" }}
              TransitionComponent={Grow}
              transitionDuration={{ enter: 200, exit: 140 }}
              PaperProps={{ className: classes.cmdMenuPaper }}
            >
              <div className={classes.cmdMenu} role="menu">
                <div className={classes.cmdMenuSection}>
                  <div className={classes.cmdMenuEyebrow}>Adicionar</div>
                  <button
                    type="button"
                    role="menuitem"
                    className={classes.cmdMenuItem}
                    onClick={() => {
                      setMobileHeaderMenuAnchor(null);
                      handleOpenContactModal();
                    }}
                  >
                    <span className={`${classes.cmdMenuIconBox} ${classes.cmdMenuIconBoxCobalt}`}>
                      <UserPlus size={16} strokeWidth={1.8} />
                    </span>
                    <span className={classes.cmdMenuLabel}>
                      {i18n.t("contacts.buttons.add")}
                    </span>
                  </button>
                  <button
                    type="button"
                    role="menuitem"
                    className={classes.cmdMenuItem}
                    onClick={() => {
                      setMobileHeaderMenuAnchor(null);
                      handleOpenImportModal();
                    }}
                  >
                    <span className={`${classes.cmdMenuIconBox} ${classes.cmdMenuIconBoxAmber}`}>
                      <Upload size={16} strokeWidth={1.8} />
                    </span>
                    <span className={classes.cmdMenuLabel}>
                      {i18n.t("contacts.buttons.import")}
                    </span>
                  </button>
                </div>

                <div className={classes.cmdMenuDivider} />

                <div className={classes.cmdMenuSection}>
                  <div className={classes.cmdMenuEyebrow}>Visualizar</div>
                  <button
                    type="button"
                    role="menuitem"
                    className={classes.cmdMenuItem}
                    onClick={() => {
                      setMobileHeaderMenuAnchor(null);
                      setOpenWalletModal(true);
                    }}
                  >
                    <span className={`${classes.cmdMenuIconBox} ${classes.cmdMenuIconBoxSlate}`}>
                      <Wallet size={16} strokeWidth={1.8} />
                    </span>
                    <span className={classes.cmdMenuLabel}>Carteira</span>
                  </button>
                </div>
              </div>
            </Popover>
          </>
        ) : (
          <>
            <Typography className={classes.title}>
              {i18n.t("contacts.title")}
            </Typography>
            <div className={classes.buttonsWrapper}>
              <TextField
                placeholder={i18n.t("contacts.searchPlaceholder")}
                type="search"
                value={searchParam}
                onChange={handleSearch}
                className={classes.searchField}
                variant="outlined"
                size="small"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search size={18} strokeWidth={1.75} color={theme.palette.type === 'dark' ? 'rgba(255, 255, 255, 0.5)' : '#64748b'} />
                    </InputAdornment>
                  ),
                }}
              />
              <button
                type="button"
                onClick={() => setOpenWalletModal(true)}
                className={`${classes.actionBtnBase} ${classes.actionBtnTertiary}`}
              >
                <Wallet size={15} strokeWidth={1.75} />
                Carteira
              </button>
              <button
                type="button"
                onClick={handleOpenImportModal}
                className={`${classes.actionBtnBase} ${classes.actionBtnSecondary}`}
              >
                <Upload size={15} strokeWidth={1.75} />
                {i18n.t("contacts.buttons.import")}
              </button>
              <button
                type="button"
                onClick={handleOpenContactModal}
                className={`${classes.actionBtnBase} ${classes.actionBtnPrimary}`}
              >
                <UserPlus size={15} strokeWidth={1.75} />
                {i18n.t("contacts.buttons.add")}
              </button>
            </div>
          </>
        )}
      </div>
      <Paper className={classes.mainPaper} style={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <div className={classes.tableContainer} style={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          {/* Header fixo */}
          <div className={classes.tableHeader}>
            <div className={classes.headerCell} style={{ width: isMobile ? 40 : 56 }} />
            <div className={classes.headerCell} style={{ flex: 1 }}>{i18n.t("contacts.table.name")}</div>
            <div
              className={classes.headerCell}
              style={isMobile ? { flex: 1, textAlign: "left" } : { width: 280, textAlign: 'center', paddingRight: 80 }}
            >
              {i18n.t("contacts.table.whatsapp")}
            </div>
            <div className={classes.headerCell} style={{ width: isMobile ? 48 : 184, textAlign: 'center' }}>
              {isMobile ? "..." : i18n.t("contacts.table.actions")}
            </div>
          </div>

          {/* Lista virtualizada */}
          <div className={classes.listWrapper}>
            {contacts.length === 0 && loading ? (
              <AutoSizer>
                {({ height, width }) => {
                  const rowHeight = isMobile ? 56 : 60;
                  const rows = Math.max(6, Math.ceil(height / rowHeight));
                  return (
                    <div style={{ width, height, overflow: 'hidden', position: 'relative' }}>
                      {Array.from({ length: rows }, (_, i) => (
                        <ContactSkeletonRow
                          key={i}
                          classes={classes}
                          isMobile={isMobile}
                          style={{ width, height: rowHeight }}
                        />
                      ))}
                      <div className={classes.skeletonFade} aria-hidden="true" />
                    </div>
                  );
                }}
              </AutoSizer>
            ) : (
              <AutoSizer>
                {({ height, width }) => (
                  <FixedSizeList
                    height={height}
                    width={width}
                    itemCount={contacts.length + (loading ? 1 : 0)}
                    itemSize={isMobile ? 56 : 60}
                    itemData={rowData}
                    itemKey={(index, data) => (
                      index >= data.contacts.length
                        ? "loading-row"
                        : data.contacts[index]?.id || index
                    )}
                    overscanCount={5}
                    onItemsRendered={({ visibleStopIndex }) => {
                      if (hasMore && !loading && visibleStopIndex >= contacts.length - 3) {
                        loadMore();
                      }
                    }}
                  >
                    {ContactVirtualRow}
                  </FixedSizeList>
                )}
              </AutoSizer>
            )}
          </div>
        </div>
      </Paper>
      <Popover
        open={isMobileActionsOpen}
        onClose={handleCloseMobileActions}
        anchorReference="anchorPosition"
        anchorPosition={mobileActionsAnchorPosition || undefined}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        TransitionComponent={Grow}
        transitionDuration={{ enter: 200, exit: 140 }}
        PaperProps={{ className: classes.cmdMenuPaper }}
      >
        <div className={classes.cmdMenu} role="menu">
          <div className={classes.cmdMenuSection}>
            <div className={classes.cmdMenuEyebrow}>Ações</div>
            <button
              type="button"
              role="menuitem"
              className={classes.cmdMenuItem}
              onClick={() => {
                if (!mobileActionsContact) return;
                handleCloseMobileActions();
                handleStartConversation(mobileActionsContact);
              }}
            >
              <span className={`${classes.cmdMenuIconBox} ${classes.cmdMenuIconBoxSlate}`}>
                <LayoutList size={16} strokeWidth={1.8} />
              </span>
              <span className={classes.cmdMenuLabel}>Ver tickets</span>
            </button>
            <button
              type="button"
              role="menuitem"
              className={classes.cmdMenuItem}
              onClick={() => {
                if (!mobileActionsContact) return;
                handleCloseMobileActions();
                handleQuickCreateTicket(mobileActionsContact);
              }}
            >
              <span className={`${classes.cmdMenuIconBox} ${classes.cmdMenuIconBoxGreen}`}>
                <MessageCirclePlus size={16} strokeWidth={1.8} />
              </span>
              <span className={classes.cmdMenuLabel}>Iniciar conversa</span>
            </button>
            <button
              type="button"
              role="menuitem"
              className={classes.cmdMenuItem}
              onClick={() => {
                if (!mobileActionsContact) return;
                hadleEditContact(mobileActionsContact.id);
                handleCloseMobileActions();
              }}
            >
              <span className={`${classes.cmdMenuIconBox} ${classes.cmdMenuIconBoxCobalt}`}>
                <Pencil size={16} strokeWidth={1.8} />
              </span>
              <span className={classes.cmdMenuLabel}>{i18n.t("contacts.buttons.edit")}</span>
            </button>
          </div>

          <Can
            role={user.profile}
            perform="contacts-page:deleteContact"
            yes={() => (
              <>
                <div className={classes.cmdMenuDivider} />
                <div className={classes.cmdMenuSection}>
                  <button
                    type="button"
                    role="menuitem"
                    className={`${classes.cmdMenuItem} ${classes.cmdMenuItemDanger}`}
                    onClick={() => {
                      if (!mobileActionsContact) return;
                      handleOpenDeleteConfirm(mobileActionsContact);
                      handleCloseMobileActions();
                    }}
                  >
                    <span className={`${classes.cmdMenuIconBox} ${classes.cmdMenuIconBoxRed}`}>
                      <Trash size={16} strokeWidth={1.8} />
                    </span>
                    <span className={classes.cmdMenuLabel}>
                      {i18n.t("contacts.buttons.deleteSingle")}
                    </span>
                  </button>
                </div>
              </>
            )}
          />
        </div>
      </Popover>
    </MainContainer>
  );
};

export default Contacts;
